import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight,
  ArrowRightLeft,
  Ban,
  Bike,
  Eye,
  FileText,
  HandCoins,
  Pencil,
  Plus,
  RefreshCcw,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import {
  SalesDocumentStatusSchema,
  type SalesDocumentSummary,
} from '@aeris/contracts'
import { formatMoney, toString } from '@aeris/money'
import { formatDocumentNumber } from '@aeris/printing'
import { parseAmount, ZERO_AMOUNT } from '@aeris/sales'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  ActiveFilters,
  FilterableHeaderCell,
  Field,
  RowActions,
  RowOpener,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableActionsCell,
  TableActionsHeaderCell,
  TableRow,
  VisuallyHidden,
  notify,
  type ActiveFilterChip,
  type RowAction,
} from '@aeris/ui'

import { AuthorisationDialog } from '../components/authorisation-dialog.tsx'
import { CourierDialog } from '../components/courier-dialog.tsx'
import { ExchangeRequestDialog } from '../components/exchange-request-dialog.tsx'
import { ExportMenu } from '../components/export-menu.tsx'
import { OrderDepositDialog } from '../components/order-deposit-dialog.tsx'
import {
  PosPaymentDialog,
  type PosTender,
} from '../components/pos-payment-dialog.tsx'
import { ChecksBadges } from '../components/sales-checks-panel.tsx'
import { SalesDocumentDialog } from '../components/sales-document-dialog.tsx'
import { SalesDocumentForm } from '../components/sales-document-form.tsx'
import { DocumentStatusBadge } from '../components/status-badge.tsx'
import { StatusLegend } from '../components/status-legend.tsx'
import { fetchCustomerCredit } from '../lib/api/exchanges.ts'
import {
  ApiError,
  convertQuote,
  dispatchSalesDocument,
  fetchCredit,
  fetchCurrentCashSession,
  fetchDepositOptions,
  fetchCancelCode,
  fetchPaymentMethods,
  fetchSalesDocument,
  fetchSalesDocuments,
  pickupSalesDocument,
  transitionSalesDocument,
  fetchSalespeople,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { enumValue, rangeLabel } from '../lib/column-filters.ts'
import { customerLookup } from '../lib/customer-lookup.ts'
import { formatDate } from '../lib/date-format.ts'
import { PERIOD_LABELS, periodRange } from '../lib/period-range.ts'
import { readRememberedTerminal } from '../lib/remembered-terminal.ts'
import { SALES_EXPORT_WITH_PAYMENT } from '../lib/sales-export.ts'
import {
  documentRef,
  KIND_LABELS,
  STATUS_LABELS,
  STOCK_STATE_LABELS,
  TRANSITION_LABELS,
} from '../lib/sales-labels.ts'
import {
  onlyInLabel,
  documentStatusMeta,
  STATUS_META,
  STATUSES_BY_KIND,
} from '../lib/sales-status-meta.tsx'
import { useDebounced } from '../lib/use-debounced.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'

/**
 * Sales.
 *
 * One screen for quotes, orders and sales, because they are one document with a
 * declared kind (ADR-0017). Three screens would advertise three domains the
 * product does not have, and "turn this quote into an order" would become a
 * navigation problem.
 *
 * The moves offered on each row come from the server's `allowedTransitions`,
 * which is computed from the same transition table the server enforces. That
 * only hides buttons; the API refuses independently, which is what actually
 * enforces the workflow.
 *
 * Amounts are formatted by `formatMoney` from `@aeris/money`, never by `Intl` —
 * which takes a `number`, and a total is never one. ADR-0016.
 */

const TIME = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
})

const PAGE_SIZE = 25

/**
 * The moves worth putting on a row.
 *
 * Not every legal transition: `draft` back from something is a correction
 * nobody makes from a list, and offering ten buttons per row makes the two that
 * matter harder to find. Cancelling is separated out because it needs a
 * different permission and a different colour.
 */
const ROW_MOVES: readonly SalesDocumentSummary['status'][] = [
  'sent',
  'accepted',
  'confirmed',
  /*
   * `fulfilled` saiu daqui — F159, fatia 3.
   *
   * "Dar saída" chamava a transição crua, que move o documento e mais nada: a
   * mercadoria ia para a rua **sem título a receber e sem passar pelo caixa**,
   * contornando a regra que a rota de despacho aplica desde a F035. O servidor
   * agora recusa, e nem anuncia mais o movimento; esta linha é a metade da tela.
   *
   * No lugar entram Retirar e Entregar, que é o que o caixa de fato faz.
   */
  'completed',
]

export const VendasPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [kind, setKind] = useState<'' | SalesDocumentSummary['kind']>('')

  /*
   * O que a legenda desta lista mostra (F156).
   *
   * Filtrada pelo tipo escolhido: com "Pedido" na barra, não faz sentido
   * explicar "Convertido", que só existe em orçamento. Sem filtro, mostra
   * tudo e diz de quem é cada exclusiva — é a resposta à pergunta que originou
   * a feature.
   */
  const legendEntries = useMemo(() => {
    const statuses =
      kind === ''
        ? SalesDocumentStatusSchema.options
        : SalesDocumentStatusSchema.options.filter((status) =>
            STATUSES_BY_KIND[kind].includes(status)
          )

    return statuses.map((status) => {
      const meta = STATUS_META[status]
      return {
        label: meta.label,
        tone: meta.tone,
        icon: meta.icon,
        what: meta.what,
        only: kind === '' ? onlyInLabel(meta, KIND_LABELS) : null,
      }
    })
  }, [kind])
  const [status, setStatus] = useState<'' | SalesDocumentSummary['status']>('')
  /*
   * Período e vendedor (F132): "quanto vendi hoje?" e "o que o João vendeu?"
   * são as perguntas de todo fim de dia, e a API já filtrava pelos dois.
   */
  const [period, setPeriod] = useState<'' | 'today' | 'last7' | 'month'>('')
  const [salespersonUserId, setSalespersonUserId] = useState('')
  /*
   * Pela forma de pagamento (F238). O gerente da Elite: "vai que a menina
   * colocou como Pix mas o cara pagou em dinheiro… aí eu vou saber por que o
   * caixa passou ou faltou". Coluna e filtro só para quem lê pagamentos.
   */
  const [paymentMethodId, setPaymentMethodId] = useState('')
  /** A faixa de valor, filtrada pela coluna Total (F152). */
  const [totalRange, setTotalRange] = useState<{
    kind: 'range'
    min: string
    max: string
  } | null>(null)
  const [page, setPage] = useState(1)

  // The list goes back to the first page when the column changes: a new
  // order with the old offset shows rows that have nothing to do with
  // what was just clicked.
  const sort = useTableSort<'issued_at' | 'number' | 'total_amount'>(
    'issued_at',
    'desc',
    () => {
      setPage(1)
    }
  )
  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  /** Ver e reimprimir, sem passar pelo formulário de edição (F150). */
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** O que acabou de acontecer, em uma frase, no molde de Orçamentos. */

  // 300 ms: long enough that a typed word is one request, short enough that the
  // list feels attached to the keyboard.
  const debouncedSearch = useDebounced(search)

  const canWrite = allows('sales.write')
  const canCancel = allows('sales.cancel')
  // O caixa (quem recebe) vê o Cancelar e a senha do supervisor libera; o
  // vendedor, não — ele não tem `finance.settle`.
  const canSettle = allows('finance.settle')
  // F126: o caixa e o vendedor pedem troca ou vale ao trocador; quem decide é ele.
  const canRequestExchange = allows('exchange.request')

  /*
   * Retirar e Entregar um PEDIDO — F159, fatia 2.
   *
   * O dono: *"o funcionario 2 - caixa vai pegar o pedido e escolher o motoboy e
   * despachar (ou retirada e pegar a forma de pagamento daí)"*. As duas ações
   * existiam só em Orçamentos e no PDV; um pedido criado pelo vendedor não
   * tinha caminho de caixa nenhum — e o botão que esta tela oferecia no lugar
   * punha mercadoria na rua sem título.
   *
   * A dupla de permissões é a mesma do PDV (ADR-0027): o vendedor promete, o
   * caixa recebe.
   */
  const canTakeMoney = allows('pos.operate') && allows('payment.write')
  const [paying, setPaying] = useState<SalesDocumentSummary | null>(null)
  /*
   * O sinal (F173): quem vende também lança, com o caixa aberto da filial.
   * A retirada, depois dele, cobra só o que falta — por isso ela lê o detalhe
   * do pedido (que traz o pago) antes de abrir o recebimento.
   */
  const canDeposit = allows('sales.deposit')
  const [depositing, setDepositing] = useState<SalesDocumentSummary | null>(
    null
  )
  const payingDetail = useQuery({
    queryKey: ['sales-document', paying?.id],
    queryFn: async ({ signal }) =>
      await fetchSalesDocument(paying?.id ?? '', signal),
    enabled: paying !== null,
  })
  /*
   * Cancelar um pedido pago pergunta o que fazer com o dinheiro (F173): o
   * servidor responde `paid_resolution_required`, e a tela pergunta.
   */
  const [resolving, setResolving] = useState<{
    document: SalesDocumentSummary
    reason: string | null
    message: string
    authorisationId?: string | null
  } | null>(null)
  /*
   * Cancelar o que segura mercadoria, ou sem `sales.cancel` (o caixa), pede a
   * senha do supervisor: o servidor responde `authorisation_required`.
   */
  const [authorising, setAuthorising] = useState<{
    document: SalesDocumentSummary
    reason: string | null
  } | null>(null)
  const [delivering, setDelivering] = useState<SalesDocumentSummary | null>(
    null
  )

  const { session: authSession } = useAuth()
  const branchId = authSession?.context?.branchId ?? undefined

  const [scanNotice, setScanNotice] = useState<string | null>(null)

  /*
   * Bipar a nota abre a venda (26/09).
   *
   * O código de barras do cupom é o NÚMERO da venda, e o leitor termina com
   * Enter. A busca já achava pelo número, mas listava — 16 trazia 116 e 160
   * junto — e o estoquista tinha de achar a linha certa. Um número que bate
   * exatamente com uma venda desta filial abre a ficha, onde ele confere.
   */
  const openScanned = (text: string): void => {
    const code = text.trim().replace(/^0+(?=\d)/, '')
    setScanNotice(null)
    if (!/^\d+$/.test(code)) return
    fetchSalesDocuments({ search: code, page: 1, pageSize: 10 })
      .then((found) => {
        const exact = found.items.filter((item) => String(item.number) === code)
        const here = exact.filter(
          (item) => branchId === undefined || item.branchId === branchId
        )
        const chosen = here.length === 1 ? here : exact
        if (chosen.length === 1 && chosen[0] !== undefined) {
          setViewingId(chosen[0].id)
        } else if (exact.length === 0) {
          setScanNotice(`Nenhuma venda com o número ${code}.`)
        }
      })
      .catch(() => {
        setScanNotice('Não foi possível procurar a nota agora.')
      })
  }
  const terminalId = readRememberedTerminal()

  /** O mesmo terminal lembrado do PDV: é a mesma máquina no mesmo balcão. */
  const till = useQuery({
    queryKey: ['cash-session', { terminalId, branchId }],
    queryFn: ({ signal }) =>
      fetchCurrentCashSession({ terminalId, branchId }, signal),
    enabled: terminalId !== '' && canTakeMoney,
  })
  const tillSession = till.data?.session ?? null

  /*
   * A loja sem caixa (F174): a retirada e o estorno não esperam caixa aberto,
   * e quem recebe o sinal também recebe o resto na retirada. As opções do
   * sinal dizem se a loja trabalha com caixa, e trazem as formas para quem
   * não lê a lista inteira.
   */
  const depositOptions = useQuery({
    queryKey: ['deposit-options'],
    queryFn: async ({ signal }) => await fetchDepositOptions(signal),
    enabled: canDeposit,
  })
  const tillLess = depositOptions.data?.usesTill === false
  const canPickup = canTakeMoney || (tillLess && canDeposit)

  const methods = useQuery({
    queryKey: ['payment-methods', { status: 'active' }],
    queryFn: ({ signal }) => fetchPaymentMethods({ status: 'active' }, signal),
    enabled: canTakeMoney,
  })

  /*
   * A loja que criou a senha de cancelamento (F294) cancela sempre com motivo
   * e senha, como o orçamento — F319. Sem a senha, o Cancelar de sempre.
   */
  const cancelCode = useQuery({
    queryKey: ['cancel-code'],
    queryFn: async () => await fetchCancelCode(),
  })
  const cancelNeedsCode = cancelCode.data?.configured === true

  // Todas as formas, as arquivadas também: a venda antiga foi paga com elas.
  const canSeePayments = allows('payment.read')
  const allMethods = useQuery({
    queryKey: ['payment-methods', {}],
    queryFn: ({ signal }) => fetchPaymentMethods({}, signal),
    enabled: canSeePayments,
  })

  const credit = useQuery({
    queryKey: ['credit', paying?.customerPartyId],
    queryFn: ({ signal }) => fetchCredit(paying?.customerPartyId ?? '', signal),
    enabled: paying !== null,
  })

  const voucher = useQuery({
    queryKey: ['customer-credit', paying?.customerPartyId],
    queryFn: ({ signal }) =>
      fetchCustomerCredit(paying?.customerPartyId ?? '', signal),
    enabled: paying !== null && allows('exchange.read'),
  })
  const [requestingFor, setRequestingFor] =
    useState<SalesDocumentSummary | null>(null)
  const [requested, setRequested] = useState<string | null>(null)

  /*
   * O filtro, montado UMA vez — F158.
   *
   * A lista e a exportação leem daqui, e é isso que impede o arquivo de sair
   * com um filtro diferente do que está na tela. Um objeto por chamada seria
   * duas verdades: a pessoa filtra o mês, exporta, e recebe o ano.
   *
   * Sem `page`/`pageSize`: quem chama decide quantas linhas quer por vez. A
   * lista pede 25; a exportação pede 100 e caminha até acabar.
   */
  const showsConverted = kind === 'quote' || status === 'converted'
  const listRequest = {
    ...(debouncedSearch.trim().length > 0
      ? { search: debouncedSearch.trim() }
      : {}),
    ...(kind === '' ? {} : { kind }),
    ...(status === '' ? {} : { status }),
    /*
     * O orçamento que virou pedido ou venda sai da lista (F162): o número
     * acompanha desde a F160, e a mesma venda aparecia duas vezes — e somava
     * duas vezes no total. Quem procura o orçamento filtra por ele.
     */
    ...(showsConverted ? {} : { hideConverted: true }),
    ...(period === ''
      ? {}
      : (() => {
          const range = periodRange(period)
          return { issuedFrom: range.from, issuedTo: range.to }
        })()),
    ...(salespersonUserId === '' ? {} : { salespersonUserId }),
    ...(paymentMethodId === '' ? {} : { paymentMethodId }),
    ...(totalRange?.min === undefined || totalRange.min === ''
      ? {}
      : { totalMin: totalRange.min }),
    ...(totalRange?.max === undefined || totalRange.max === ''
      ? {}
      : { totalMax: totalRange.max }),
    sort: sort.sort,
    direction: sort.direction,
  }

  const query = useQuery({
    queryKey: [
      'sales-documents',
      {
        search: debouncedSearch,
        kind,
        status,
        period,
        salespersonUserId,
        paymentMethodId,
        // Sem o filtro na chave, o cache devolveria a lista sem ele.
        totalMin: totalRange?.min ?? '',
        totalMax: totalRange?.max ?? '',
        page,
        ...sortKey(sort),
      },
    ],
    queryFn: ({ signal }) =>
      fetchSalesDocuments(
        { ...listRequest, page, pageSize: PAGE_SIZE },
        signal
      ),
    // Keeps the previous page on screen while the next one loads, so the table
    // does not collapse to a spinner on every keystroke.
    placeholderData: (previous) => previous,
  })

  const salespeople = useQuery({
    queryKey: ['salespeople'],
    queryFn: ({ signal }) => fetchSalespeople(signal),
  })

  const editing = useQuery({
    queryKey: ['sales-document', editingId],
    queryFn: ({ signal }) => fetchSalesDocument(editingId ?? '', signal),
    enabled: editingId !== null,
  })

  /**
   * Depois de tirar mercadoria ou receber dinheiro, quatro listas ficam velhas.
   *
   * Não só a de vendas: o estoque baixou, a gaveta mudou e — na entrega — um
   * título nasceu. Invalidar só a lista deixaria o Financeiro mostrando o
   * mundo de antes, e é onde o caixa vai olhar em seguida.
   */
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
    void queryClient.invalidateQueries({ queryKey: ['stock'] })
    void queryClient.invalidateQueries({ queryKey: ['cash-session'] })
    void queryClient.invalidateQueries({ queryKey: ['finance'] })
  }

  const move = useMutation({
    mutationFn: async (input: {
      document: SalesDocumentSummary
      to: SalesDocumentSummary['status']
      reason?: string | null
      authorisationId?: string | null
    }) =>
      await transitionSalesDocument(input.document.id, {
        version: input.document.version,
        to: input.to,
        reason: input.reason ?? null,
        authorisationId: input.authorisationId ?? null,
      }),
    onSuccess: async (_response, input) => {
      setError(null)
      // O retorno à vista (F248): Confirmar e Concluir agiam calados.
      notify.success(
        `${documentRef(input.document.kind, input.document.number)} agora está “${STATUS_META[input.to].label}”.`
      )
      await queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
    },
    onError: (cause: unknown) => {
      /**
       * The refusal is shown, not swallowed.
       *
       * A transition the workflow does not allow comes back naming what was
       * refused and what is available instead — an operator seeing nothing
       * happen has no way to tell a business rule from a bug.
       */
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível mover o documento.'
      )
    },
  })

  /** O cliente levou e pagou agora: sai do estoque e entra na gaveta. */
  const pickup = useMutation({
    mutationFn: async (input: {
      document: SalesDocumentSummary
      tenders: readonly PosTender[]
      creditAuthorisationId?: string | null
    }) =>
      await pickupSalesDocument(input.document.id, {
        version: input.document.version,
        cashSessionId: tillLess ? null : (tillSession?.id ?? null),
        payments: input.tenders.map((tender) => ({
          paymentMethodId: tender.methodId,
          amount: toString(tender.amount),
        })),
        creditAuthorisationId: input.creditAuthorisationId ?? null,
      }),
    onSuccess: async (response) => {
      setError(null)
      setPaying(null)
      notify.success(
        `Pedido nº ${formatDocumentNumber(response.document.number, '')} retirado e pago. A mercadoria saiu do estoque.`
      )
      await refresh()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível concluir a retirada.'
      )
    },
  })

  /**
   * Cancelar: pede a senha do supervisor quando o servidor exige, e pergunta
   * do dinheiro quando o pedido está pago (F173).
   */
  const cancelDocument = (
    document: SalesDocumentSummary,
    reason: string | null,
    authorisationId: string | null
  ): void => {
    move.mutate(
      { document, to: 'cancelled', reason, authorisationId },
      {
        onError: (cause: unknown) => {
          if (!(cause instanceof ApiError)) return
          if (
            cause.code === 'authorisation_required' &&
            authorisationId === null
          ) {
            setError(null)
            setAuthorising({ document, reason })
          } else if (cause.code === 'paid_resolution_required') {
            setError(null)
            setResolving({
              document,
              reason,
              message: cause.message,
              authorisationId,
            })
          }
        },
        onSettled: () => {
          setCancelling(null)
        },
      }
    )
  }

  /** Cancelar o pedido pago, já com a resposta sobre o dinheiro (F173). */
  const cancelPaid = useMutation({
    mutationFn: async (input: {
      document: SalesDocumentSummary
      reason: string | null
      resolution: 'refund' | 'voucher'
      authorisationId?: string | null
      refundMethodId?: string | null
    }) =>
      await transitionSalesDocument(input.document.id, {
        version: input.document.version,
        to: 'cancelled',
        reason: input.reason,
        authorisationId: input.authorisationId ?? null,
        paidResolution: input.resolution,
        refundMethodId:
          input.resolution === 'refund' ? (input.refundMethodId ?? null) : null,
        cashSessionId:
          input.resolution === 'refund' && !tillLess
            ? (tillSession?.id ?? null)
            : null,
      }),
    onSuccess: async (_response, input) => {
      setError(null)
      setResolving(null)
      notify.success(
        input.resolution === 'refund'
          ? `${documentRef(input.document.kind, input.document.number)} cancelado. O dinheiro foi devolvido.`
          : `${documentRef(input.document.kind, input.document.number)} cancelado. O que o cliente pagou virou vale.`
      )
      await refresh()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível cancelar o pedido.'
      )
    },
  })

  /** Sai com o motoboy: o dinheiro vira título e o acerto é depois. */
  const deliver = useMutation({
    mutationFn: async (input: {
      document: SalesDocumentSummary
      courierPartyId: string
    }) =>
      await dispatchSalesDocument(input.document.id, {
        version: input.document.version,
        courierPartyId: input.courierPartyId,
      }),
    onSuccess: async (response) => {
      setError(null)
      setDelivering(null)
      notify.success(
        `Pedido nº ${formatDocumentNumber(response.document.number, '')} despachado. O título está em Financeiro, e o acerto é na tela do entregador.`
      )
      await refresh()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível despachar a entrega.'
      )
    },
  })

  const convert = useMutation({
    mutationFn: async (document: SalesDocumentSummary) =>
      await convertQuote(document.id, document.version),
    onSuccess: async (response) => {
      setError(null)
      // O número do pedido que nasceu, para achá-lo na lista (F248).
      notify.success(
        `${documentRef(response.quote.kind, response.quote.number)} virou o ${documentRef(response.order.kind, response.order.number)}.`
      )
      await queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível converter o orçamento.'
      )
    },
  })

  const total = query.data?.total ?? 0
  const items = query.data?.items ?? []
  const filtered =
    debouncedSearch.trim().length > 0 ||
    kind !== '' ||
    status !== '' ||
    period !== '' ||
    salespersonUserId !== '' ||
    paymentMethodId !== '' ||
    totalRange !== null

  const clearFilters = (): void => {
    setSearch('')
    setKind('')
    setStatus('')
    setPeriod('')
    setSalespersonUserId('')
    setPaymentMethodId('')
    setTotalRange(null)
    setPage(1)
  }

  /*
   * O que está filtrado, à vista (F152).
   *
   * Com o filtro escondido num menu de cabeçalho, esquecer um ligado deixa de
   * ser hipótese: a pessoa procura uma venda que existe, não acha, e conclui
   * que sumiu.
   */
  const chips: ActiveFilterChip[] = [
    ...(kind === ''
      ? []
      : [
          {
            id: 'kind',
            label: `Tipo: ${KIND_LABELS[kind]}`,
            onRemove: () => {
              setKind('')
              setPage(1)
            },
          },
        ]),
    ...(status === ''
      ? []
      : [
          {
            id: 'status',
            label: `Situação: ${STATUS_LABELS[status]}`,
            onRemove: () => {
              setStatus('')
              setPage(1)
            },
          },
        ]),
    ...(salespersonUserId === ''
      ? []
      : [
          {
            id: 'salesperson',
            label: `Vendedor: ${
              (salespeople.data?.items ?? []).find(
                (person) => person.userId === salespersonUserId
              )?.displayName ?? 'escolhido'
            }`,
            onRemove: () => {
              setSalespersonUserId('')
              setPage(1)
            },
          },
        ]),
    ...(paymentMethodId === ''
      ? []
      : [
          {
            id: 'payment',
            label: `Pagamento: ${
              (allMethods.data?.methods ?? []).find(
                (method) => method.id === paymentMethodId
              )?.name ?? 'escolhido'
            }`,
            onRemove: () => {
              setPaymentMethodId('')
              setPage(1)
            },
          },
        ]),
    ...(period === ''
      ? []
      : [
          {
            id: 'period',
            label: `Período: ${PERIOD_LABELS[period]}`,
            onRemove: () => {
              setPeriod('')
              setPage(1)
            },
          },
        ]),
    ...(totalRange === null
      ? []
      : [
          {
            id: 'total',
            label: `Valor: ${rangeLabel(totalRange.min, totalRange.max)}`,
            onRemove: () => {
              setTotalRange(null)
              setPage(1)
            },
          },
        ]),
  ]

  /*
   * Cancelar pede confirmação e motivo (F132). Cancelar devolve estoque e, com
   * pagamento, dinheiro: um clique solto numa linha de uma lista não pode fazer
   * isso, e o motivo fixo "Cancelado na tela de vendas" não explicava nada a
   * quem lê o histórico depois.
   */
  const [cancelling, setCancelling] = useState<SalesDocumentSummary | null>(
    null
  )

  return (
    <Page>
      <PageHeader
        title="Vendas"
        description="Orçamentos, pedidos e vendas. O preço fica gravado no item quando ele é adicionado."
        actions={
          <>
            {/* O "?" da barra: quais situações existem nesta lista e o que cada
                uma quer dizer (F156). Antes do botão primário, porque explicar
                a tela não compete com a ação da tela. */}
            <StatusLegend entries={legendEntries} />
            {canWrite ? (
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  setEditingId(null)
                  setFormOpen(true)
                }}
              >
                Novo documento
              </Button>
            ) : null}
          </>
        }
      />

      <ListToolbar>
        <SearchInput
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setPage(1)
          }}
          placeholder="Bipe a nota, ou busque por número ou cliente"
          aria-label="Buscar documentos"
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            openScanned(search)
          }}
        />

        {/*
          Tipo, Situação, Vendedor e Total moraram aqui até a F152 e agora são
          filtro de coluna. Um filtro, um controle: com os dois, mudar no
          cabeçalho deixaria o select da barra mentindo.

          O PERÍODO continua aqui de propósito. "Hoje" e "Este mês" são um
          clique; a mesma pergunta no cabeçalho seria digitar duas datas.
        */}
        <Select
          value={period}
          aria-label="Período"
          className="w-40"
          onChange={(event) => {
            setPeriod(event.target.value as '' | 'today' | 'last7' | 'month')
            setPage(1)
          }}
        >
          <option value="">Qualquer data</option>
          {(['today', 'last7', 'month'] as const).map((value) => (
            <option key={value} value={value}>
              {PERIOD_LABELS[value]}
            </option>
          ))}
        </Select>

        {/*
          O arquivo leva o FILTRO INTEIRO, não a página — F158. Quem filtra
          setembro e exporta recebe setembro inteiro, não os 25 da tela.
        */}
        <ExportMenu
          fileName="vendas"
          sheetName="Vendas"
          columns={SALES_EXPORT_WITH_PAYMENT}
          fetchPage={async (exportPage) =>
            await fetchSalesDocuments({
              ...listRequest,
              page: exportPage,
              pageSize: 100,
            })
          }
          pageSize={100}
          total={total}
        />
      </ListToolbar>

      {scanNotice === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {scanNotice}
        </p>
      )}

      <ActiveFilters chips={chips} onClearAll={clearFilters} />

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <ResultCount
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          loading={query.isPending}
          none="Nenhum documento encontrado"
        />
        {query.data === undefined ? null : (
          <p className="text-(length:--text-xs) text-(--text-muted)">
            {/* The whole filter, not this page — and cancelled documents count
                for nothing, because a cancelled sale is not revenue. */}
            Total do filtro:{' '}
            <strong className="font-(--font-mono) text-(--text-primary)">
              {formatMoney(parseAmount(query.data.totalAmount) ?? ZERO_AMOUNT)}
            </strong>
          </p>
        )}
        {showsConverted ? null : (
          <p className="text-(length:--text-xs) text-(--text-muted)">
            Orçamentos que viraram pedido ou venda ficam em Orçamentos.
          </p>
        )}
      </div>

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      {/* Perto da lista, e não depois da paginação: é a resposta ao clique. */}
      {requested === null ? null : (
        <InlineAlert tone="success">{requested}</InlineAlert>
      )}

      {/* O que a retirada ou a entrega acabou de fazer, e onde olhar depois. */}

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="os documentos"
        isEmpty={items.length === 0}
        filtered={filtered}
        onClearFilters={clearFilters}
        empty={
          <EmptyState
            icon={<FileText aria-hidden="true" className="h-8 w-8" />}
            title="Nenhum documento ainda"
            description={
              canWrite
                ? 'Crie o primeiro orçamento, pedido ou venda.'
                : 'Você não tem permissão para criar documentos de venda.'
            }
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <FilterableHeaderCell kind="number" {...sort.column('number')}>
                  Nº
                </FilterableHeaderCell>
                <FilterableHeaderCell
                  filter={{
                    kind: 'enum',
                    options: (['quote', 'order', 'sale'] as const).map(
                      (value) => ({ value, label: KIND_LABELS[value] })
                    ),
                  }}
                  {...(kind === '' ? {} : { value: enumValue(kind) })}
                  onFilter={(next) => {
                    setKind(
                      (next?.kind === 'enum' ? next.value : '') as
                        '' | SalesDocumentSummary['kind']
                    )
                    setPage(1)
                  }}
                >
                  Tipo
                </FilterableHeaderCell>
                <TableHeaderCell>Cliente</TableHeaderCell>
                <FilterableHeaderCell
                  filter={{
                    kind: 'enum',
                    options: (salespeople.data?.items ?? []).map((person) => ({
                      value: person.userId,
                      label: person.displayName,
                    })),
                  }}
                  {...(salespersonUserId === ''
                    ? {}
                    : { value: enumValue(salespersonUserId) })}
                  onFilter={(next) => {
                    setSalespersonUserId(
                      next?.kind === 'enum' ? next.value : ''
                    )
                    setPage(1)
                  }}
                >
                  Vendedor
                </FilterableHeaderCell>
                <FilterableHeaderCell kind="date" {...sort.column('issued_at')}>
                  Emissão
                </FilterableHeaderCell>
                <FilterableHeaderCell
                  filter={{
                    kind: 'enum',
                    options: (
                      Object.keys(
                        STATUS_LABELS
                      ) as SalesDocumentSummary['status'][]
                    ).map((value) => ({ value, label: STATUS_LABELS[value] })),
                  }}
                  {...(status === '' ? {} : { value: enumValue(status) })}
                  onFilter={(next) => {
                    setStatus(
                      (next?.kind === 'enum' ? next.value : '') as
                        '' | SalesDocumentSummary['status']
                    )
                    setPage(1)
                  }}
                >
                  Situação
                </FilterableHeaderCell>
                <FilterableHeaderCell
                  numeric
                  {...sort.column('total_amount')}
                  filter={{ kind: 'range', unit: 'money' }}
                  {...(totalRange === null ? {} : { value: totalRange })}
                  onFilter={(next) => {
                    setTotalRange(next?.kind === 'range' ? next : null)
                    setPage(1)
                  }}
                >
                  Total
                </FilterableHeaderCell>
                {canSeePayments ? (
                  <FilterableHeaderCell
                    filter={{
                      kind: 'enum',
                      options: (allMethods.data?.methods ?? []).map(
                        (method) => ({ value: method.id, label: method.name })
                      ),
                    }}
                    {...(paymentMethodId === ''
                      ? {}
                      : { value: enumValue(paymentMethodId) })}
                    onFilter={(next) => {
                      setPaymentMethodId(
                        next?.kind === 'enum' ? next.value : ''
                      )
                      setPage(1)
                    }}
                  >
                    Pagamento
                  </FilterableHeaderCell>
                ) : null}
                <TableHeaderCell>Conferência</TableHeaderCell>
                {canWrite ? (
                  // Presa à direita (F248): no tablet, a ação fica à vista.
                  <TableActionsHeaderCell>
                    <VisuallyHidden>Ações</VisuallyHidden>
                  </TableActionsHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((document) => {
                /*
                 * Um botão à vista, o resto no ⋯ (F132). O à vista é o próximo
                 * passo natural do documento: editar o rascunho, converter o
                 * orçamento, ou o primeiro movimento que o fluxo permite.
                 */
                const moves = document.allowedTransitions.filter((next) =>
                  ROW_MOVES.includes(next)
                )
                const canConvert =
                  document.allowedTransitions.includes('converted')
                // O servidor diz o que ESTA pessoa pode mudar (F147): o
                // orçamento dela, ou tudo, para quem gerencia.
                const primaryKind: 'edit' | 'convert' | 'move' | null =
                  document.editScope !== 'none'
                    ? 'edit'
                    : canConvert
                      ? 'convert'
                      : moves.length > 0
                        ? 'move'
                        : null
                const [firstMove] = moves

                const primary =
                  primaryKind === 'edit' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      leadingIcon={
                        <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
                      }
                      onClick={() => {
                        setEditingId(document.id)
                        setFormOpen(true)
                      }}
                    >
                      Editar
                    </Button>
                  ) : primaryKind === 'convert' ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={convert.isPending}
                      leadingIcon={
                        <ArrowRightLeft
                          aria-hidden="true"
                          className="h-3.5 w-3.5"
                        />
                      }
                      onClick={() => {
                        convert.mutate(document)
                      }}
                    >
                      Converter em pedido
                    </Button>
                  ) : primaryKind === 'move' && firstMove !== undefined ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={move.isPending}
                      onClick={() => {
                        move.mutate({ document, to: firstMove })
                      }}
                    >
                      {TRANSITION_LABELS[firstMove]}
                    </Button>
                  ) : undefined

                const menu: (RowAction | false)[] = [
                  {
                    // Ver e reimprimir (F150). Primeiro porque é a única ação
                    // que não muda nada — e a única que existe para uma venda
                    // já concluída, que não aceita mais transição nenhuma.
                    label: 'Ver detalhes',
                    icon: <Eye aria-hidden="true" className="h-4 w-4" />,
                    onSelect: () => {
                      setViewingId(document.id)
                    },
                  },
                  /*
                   * O que o caixa faz com um pedido — F159, fatia 2.
                   *
                   * Logo abaixo de "Ver detalhes" porque são as duas ações que
                   * esta linha existe para oferecer a quem opera a gaveta. Só
                   * em pedido que ainda não saiu: depois de `fulfilled` a
                   * mercadoria já está fora, e o que resta é acertar o título.
                   *
                   * "Retirar" fica desabilitado sem caixa aberto em vez de
                   * sumir: sumir faria o operador procurar um botão que existe.
                   */
                  canDeposit &&
                    document.kind === 'order' &&
                    document.status === 'confirmed' && {
                      label: 'Receber sinal',
                      icon: (
                        <HandCoins aria-hidden="true" className="h-4 w-4" />
                      ),
                      onSelect: () => {
                        setDepositing(document)
                      },
                    },
                  canPickup &&
                    document.kind === 'order' &&
                    (document.status === 'draft' ||
                      document.status === 'confirmed') && {
                      label: 'Retirar',
                      icon: (
                        <ArrowRight aria-hidden="true" className="h-4 w-4" />
                      ),
                      disabled:
                        pickup.isPending || (!tillLess && tillSession === null),
                      onSelect: () => {
                        setPaying(document)
                      },
                    },
                  canTakeMoney &&
                    document.kind === 'order' &&
                    (document.status === 'draft' ||
                      document.status === 'confirmed') && {
                      label: 'Entregar',
                      icon: <Bike aria-hidden="true" className="h-4 w-4" />,
                      disabled: deliver.isPending,
                      onSelect: () => {
                        setDelivering(document)
                      },
                    },
                  ...moves
                    .filter(
                      (next) => !(primaryKind === 'move' && next === firstMove)
                    )
                    .map((next) => ({
                      label: TRANSITION_LABELS[next],
                      disabled: move.isPending,
                      onSelect: () => {
                        move.mutate({ document, to: next })
                      },
                    })),
                  primaryKind !== 'convert' &&
                    canConvert && {
                      label: 'Converter em pedido',
                      icon: (
                        <ArrowRightLeft
                          aria-hidden="true"
                          className="h-4 w-4"
                        />
                      ),
                      onSelect: () => {
                        convert.mutate(document)
                      },
                    },
                  /*
                   * O pedido de troca ou vale (F126, revisto na F159).
                   *
                   * Quem responde "dá para trocar?" é o servidor, em
                   * `exchangeScope`. Aqui havia `kind === 'sale' && status ===
                   * 'completed'`, que escondia o item em todo pedido entregue —
                   * ou seja, em quase toda venda desta loja.
                   */
                  canRequestExchange &&
                    document.exchangeScope === 'allowed' && {
                      label: 'Pedir troca',
                      icon: (
                        <RefreshCcw aria-hidden="true" className="h-4 w-4" />
                      ),
                      onSelect: () => {
                        setRequested(null)
                        setRequestingFor(document)
                      },
                    },
                  // `sales.cancel` é outra permissão: cancelar devolve estoque
                  // e — com pagamento — dinheiro.
                  // Sem ela (o caixa), o botão aparece e a senha do
                  // supervisor libera — o servidor é quem decide.
                  (canCancel || canSettle) &&
                    document.allowedTransitions.includes('cancelled') && {
                      label: 'Cancelar',
                      danger: true,
                      icon: <Ban aria-hidden="true" className="h-4 w-4" />,
                      onSelect: () => {
                        if (cancelNeedsCode) {
                          setAuthorising({ document, reason: null })
                        } else {
                          setCancelling(document)
                        }
                      },
                    },
                ]

                return (
                  <TableRow
                    key={document.id}
                    onOpen={() => {
                      setViewingId(document.id)
                    }}
                  >
                    <TableCell className="font-(--font-mono) whitespace-nowrap">
                      <RowOpener
                        onOpen={() => {
                          setViewingId(document.id)
                        }}
                      >
                        {formatDocumentNumber(document.number)}
                      </RowOpener>
                    </TableCell>
                    <TableCell>{KIND_LABELS[document.kind]}</TableCell>
                    <TableCell>{document.customerName}</TableCell>
                    <TableCell className="text-(--text-secondary)">
                      {document.salespersonName ?? '—'}
                    </TableCell>
                    {/* Com a hora: três vendas no dia para o mesmo cliente
                        eram três linhas iguais. */}
                    <TableCell className="whitespace-nowrap text-(--text-secondary)">
                      {formatDate(document.issuedAt)}{' '}
                      <span className="text-(--text-muted)">
                        {TIME.format(new Date(document.issuedAt))}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DocumentStatusBadge
                        meta={documentStatusMeta(document)}
                      />
                      {document.stockState === 'reserved' ? (
                        <Badge tone="info" className="ml-2">
                          {STOCK_STATE_LABELS.reserved}
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell numeric>
                      {formatMoney(
                        parseAmount(document.totalAmount) ?? ZERO_AMOUNT
                      )}
                    </TableCell>
                    {canSeePayments ? (
                      <TableCell className="text-(--text-secondary)">
                        {document.paymentMethods === null ||
                        document.paymentMethods.length === 0
                          ? '—'
                          : document.paymentMethods.join(' + ')}
                      </TableCell>
                    ) : null}
                    <TableCell className="whitespace-nowrap">
                      <ChecksBadges document={document} />
                    </TableCell>
                    {canWrite ? (
                      <TableActionsCell>
                        <RowActions
                          name={`documento nº ${formatDocumentNumber(document.number)}`}
                          primary={primary}
                          items={menu}
                        />
                      </TableActionsCell>
                    ) : null}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <ConfirmDialog
        open={cancelling !== null}
        onOpenChange={(next) => {
          if (!next) setCancelling(null)
        }}
        title="Cancelar documento"
        description={
          cancelling === null
            ? ''
            : `Cancelar ${KIND_LABELS[cancelling.kind].toLowerCase()} nº ${formatDocumentNumber(cancelling.number)} de ${cancelling.customerName}? O estoque reservado ou baixado volta, e o cancelamento fica no histórico com o motivo.`
        }
        confirmLabel="Cancelar documento"
        cancelLabel="Voltar"
        reason={{
          label: 'Motivo',
          placeholder: 'Ex.: cliente desistiu',
          required: true,
        }}
        loading={move.isPending}
        onConfirm={(reason) => {
          if (cancelling === null) return
          cancelDocument(cancelling, reason?.trim() ?? null, null)
        }}
      />

      {authorising === null ? null : (
        <AuthorisationDialog
          title={`Cancelar ${KIND_LABELS[authorising.document.kind].toLowerCase()} nº ${formatDocumentNumber(authorising.document.number)}`}
          description={
            authorising.document.stockState === 'reserved'
              ? 'Este documento está segurando mercadoria. Ela volta ao estoque, e um supervisor precisa autorizar.'
              : cancelNeedsCode
                ? `${authorising.document.customerName}: o estoque volta, e o cancelamento fica no histórico com o motivo e quem autorizou.`
                : 'Um supervisor precisa autorizar o cancelamento.'
          }
          action="sales.cancel"
          targetType="sales_document"
          targetId={authorising.document.id}
          onAuthorised={(authorisationId, reason) => {
            const { document } = authorising
            const why = authorising.reason ?? reason
            setAuthorising(null)
            cancelDocument(document, why, authorisationId)
          }}
          onClose={() => {
            setAuthorising(null)
          }}
        />
      )}

      {resolving === null ? null : (
        <PaidCancelDialog
          message={resolving.message}
          canRefund={canTakeMoney}
          tillOpen={tillLess || tillSession !== null}
          refundMethods={(methods.data?.methods ?? []).filter(
            (method) => !method.generatesReceivable && method.kind !== 'voucher'
          )}
          pending={cancelPaid.isPending}
          error={error}
          onChoose={(resolution, refundMethodId) => {
            cancelPaid.mutate({
              document: resolving.document,
              reason: resolving.reason,
              authorisationId: resolving.authorisationId ?? null,
              resolution,
              refundMethodId,
            })
          }}
          onClose={() => {
            setResolving(null)
          }}
        />
      )}

      {depositing === null ? null : (
        <OrderDepositDialog
          orderId={depositing.id}
          customerPartyId={depositing.customerPartyId}
          onClose={() => {
            setDepositing(null)
          }}
          onDone={(message) => {
            setDepositing(null)
            notify.success(message)
            void refresh()
          }}
        />
      )}

      {paying === null || payingDetail.data === undefined ? null : (
        <PosPaymentDialog
          total={
            parseAmount(payingDetail.data.document.outstandingAmount) ??
            ZERO_AMOUNT
          }
          methods={
            canTakeMoney
              ? (methods.data?.methods ?? [])
              : (depositOptions.data?.methods ?? [])
          }
          credit={credit.data ?? null}
          voucherBalance={voucher.data?.balance ?? null}
          voucherLookup={customerLookup(voucher, {
            hasCustomer: true,
            allowed: allows('exchange.read'),
          })}
          creditLookup={customerLookup(credit, {
            hasCustomer: true,
            allowed: true,
          })}
          pending={pickup.isPending}
          error={error}
          onConfirm={(tenders, creditAuthorisationId) => {
            pickup.mutate({ document: paying, tenders, creditAuthorisationId })
          }}
          onClose={() => {
            setPaying(null)
          }}
        />
      )}

      {delivering === null ? null : (
        <CourierDialog
          title={`Entregar o pedido nº ${formatDocumentNumber(delivering.number, '')}`}
          pending={deliver.isPending}
          error={error}
          onChoose={(courierPartyId) => {
            deliver.mutate({ document: delivering, courierPartyId })
          }}
          onClose={() => {
            setDelivering(null)
          }}
        />
      )}

      {requestingFor === null ? null : (
        <ExchangeRequestDialog
          sale={{
            id: requestingFor.id,
            kind: requestingFor.kind,
            number: requestingFor.number,
            customerName: requestingFor.customerName,
          }}
          onClose={() => {
            setRequestingFor(null)
          }}
          onDone={() => {
            setRequested(
              `Pedido de troca da venda nº ${formatDocumentNumber(requestingFor.number)} enviado ao trocador.`
            )
            setRequestingFor(null)
          }}
        />
      )}

      {viewingId === null ? null : (
        <SalesDocumentDialog
          documentId={viewingId}
          onClose={() => {
            setViewingId(null)
          }}
          {...(canWrite
            ? {
                onEdit: () => {
                  setEditingId(viewingId)
                  setFormOpen(true)
                  setViewingId(null)
                },
              }
            : {})}
        />
      )}

      {/* Mounted only while open, and keyed by the document being edited, so
          the form starts from the right values without an effect that resets
          them. React's own answer to "reset state when a prop changes". */}
      {formOpen && (editingId === null || editing.data !== undefined) ? (
        <SalesDocumentForm
          key={editingId ?? 'new'}
          document={editingId === null ? undefined : editing.data?.document}
          onClose={() => {
            setFormOpen(false)
            setEditingId(null)
          }}
        />
      ) : null}
    </Page>
  )
}

/**
 * O pedido pago que se cancela: o dinheiro volta ou vira vale (F173).
 *
 * A decisão é de quem cancela, na hora — "sinal não se devolve" é regra de
 * cada loja, e não do sistema. Devolver dinheiro é do caixa (e pede o caixa
 * aberto, de onde o dinheiro sai); vale não mexe na gaveta.
 */
const PaidCancelDialog = ({
  message,
  canRefund,
  tillOpen,
  refundMethods,
  pending,
  error,
  onChoose,
  onClose,
}: {
  message: string
  canRefund: boolean
  tillOpen: boolean
  /** Por onde o dinheiro pode voltar (F319): dinheiro, Pix, cartão. */
  refundMethods: readonly { id: string; name: string }[]
  pending: boolean
  error: string | null
  onChoose: (
    resolution: 'refund' | 'voucher',
    refundMethodId: string | null
  ) => void
  onClose: () => void
}): ReactNode => {
  /*
   * Por onde voltou (F319): o pedido 686 foi pago em dinheiro e devolvido no
   * Pix. Vazio, cada pagamento volta pela forma em que entrou.
   */
  const [via, setVia] = useState('')
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="O que fazer com o que foi pago?"
        description={message}
      >
        {canRefund ? null : (
          <InlineAlert tone="info">
            Devolver dinheiro é do caixa. Você pode transformar em vale, ou
            pedir a quem opera o caixa para cancelar devolvendo.
          </InlineAlert>
        )}
        {canRefund && !tillOpen ? (
          <InlineAlert tone="info">
            Para devolver em dinheiro, abra o caixa neste computador. Pagamento
            por PIX ou cartão é estornado sem mexer na gaveta.
          </InlineAlert>
        ) : null}
        {canRefund && refundMethods.length > 0 ? (
          <Field
            label="Devolveu por"
            hint="Se o dinheiro voltou por outra forma — pagou em dinheiro e devolveu no Pix —, escolha aqui. A gaveta só mexe quando volta em dinheiro."
          >
            {(field) => (
              <Select
                {...field}
                value={via}
                onChange={(event) => {
                  setVia(event.target.value)
                }}
              >
                <option value="">A mesma forma em que pagou</option>
                {refundMethods.map((method) => (
                  <option key={method.id} value={method.id}>
                    {method.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}
        {error === null ? null : (
          <InlineAlert tone="danger">{error}</InlineAlert>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            variant="secondary"
            disabled={pending}
            onClick={() => {
              onChoose('voucher', null)
            }}
          >
            Virar vale do cliente
          </Button>
          {canRefund ? (
            <Button
              variant="danger"
              disabled={pending}
              onClick={() => {
                onChoose('refund', via === '' ? null : via)
              }}
            >
              Devolver o dinheiro
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
